import dayjs from 'dayjs';
import Image from 'next/image';

import {
  Breadcrumbs,
  Carousel,
  Container,
  MembershipLabel,
  RichText,
  SectionTitle,
  SectionWrapper,
  SocialSharing,
} from '@/components';
import {
  ArticleWrapper,
  Content,
  CustomBannerAdsense,
  DateTime,
  ImageWrapper,
  InnerContent,
  LeftSection,
  SubSectionWrapper,
  Title,
} from './Article.styles';

import {
  type IArticleProps,
  type IArticleWithoutSensitiveDataProps,
  PMCAdType,
  ResponsiveCollection,
  type ThumbnailProps,
} from '@/interfaces';

import { useGlobalState, useResponsive } from '@/hooks';

import { RichTextEntries } from '@/constants';
import { articleRichTextGTMButtonModify, checkIsActualDownloadButton } from '@/utils';

const Article: React.FC<{
  articleContent: IArticleProps;
  relatedArticles: ThumbnailProps[];
}> = ({ articleContent, relatedArticles }) => {
  const { title, date, image, content, breadcrumbsTheme } = articleContent;
  const { pageUrl, isLoggedIn, getProperImageUrl } = useGlobalState();
  const { isMobile } = useResponsive([ResponsiveCollection.Mobile]);

  const formattedDate = date && dayjs(date).format('MMM DD, YYYY');
  const isNotFreeArticle = content?.links?.entries?.block?.some((block) => {
    const isActualDownloadButton = block?.__typename === RichTextEntries.Button && checkIsActualDownloadButton(block);

    return isActualDownloadButton;
  });

  const contentModified = content && articleRichTextGTMButtonModify(content, isLoggedIn);

  return (
    <ArticleWrapper>
      <Container>
        <Breadcrumbs title={title} breadcrumbsTheme={breadcrumbsTheme} />
        <InnerContent>
          <LeftSection>
            <SectionWrapper>
              {isNotFreeArticle && <MembershipLabel />}
              <Title>{title}</Title>
              <DateTime>{formattedDate}</DateTime>
              {image && (
                <ImageWrapper>
                  <Image
                    src={getProperImageUrl(image)}
                    alt={title || 'Article Image'}
                    fill
                    sizes='100vw'
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                  />
                </ImageWrapper>
              )}
              {contentModified && (
                <Content className='custom__richtext'>
                  <RichText
                    richText={contentModified}
                    hasWall={(articleContent as IArticleWithoutSensitiveDataProps).hasWall}
                    pageTitle={title}
                  />
                </Content>
              )}
              <SocialSharing url={pageUrl} />
            </SectionWrapper>
            <CustomBannerAdsense variant={PMCAdType.LEFT_SECTION} />
            <SubSectionWrapper>
              <SectionTitle>Also worth your time</SectionTitle>
              <Carousel carouselData={relatedArticles} slidesToShow={isMobile ? 2 : 3} />
            </SubSectionWrapper>
          </LeftSection>
        </InnerContent>
      </Container>
    </ArticleWrapper>
  );
};

export default Article;
